import { store } from 'src/store';
import { hasText } from 'src/utils';
import { addSnackbar, doLogout } from 'src/domains/global/actions';
import wretch, { WretchResponse } from "wretch";
import QueryStringAddon from "wretch/addons/queryString";
import _, { Dictionary } from "lodash";
import { addLogEntry } from 'src/domains/networklog/actions';
import { RequestConf } from '.';

export interface GenericHttpResponse {
    status: number;
    headers: Headers;
    json: any;
    blob: any;
}

const baseReq = () => {
    let token = store.getState().global.login.token
    return wretch(store.getState().global.options.baseUrl!)
        .addon(QueryStringAddon)
        .auth(`${token?.token_type} ${token?.access_token}`)
        .accept('application/json')
        .options({ mode: 'cors' });
}

type HttpMethod = "get" | "post" | "put" | "patch" | "delete";

/**
 * HTTP -----------------------------------------------
 *
 * @param pathTpl a template from OpenAPI doc
 * @param pathParams path params
 * @param queryParams query params
 * @param body body
 */
const runMethod: (
    httpMethod: HttpMethod,
    pathTpl: string,
    pathParams: Dictionary<string>,
    queryParams: Dictionary<string>,
    body: any,
    contentType?: string,
    conf?: RequestConf
) => Promise<GenericHttpResponse> = async (
    httpMethod,
    pathTpl,
    pathParams,
    queryParams,
    body,
    contentType,
    conf = {}
) => {
        const compiled = _.template(pathTpl);
        const path = compiled(pathParams);
        store.dispatch(addLogEntry({
            method: httpMethod,
            path,
            queryParams,
            body
        }))

        try {
            const request = baseReq()
                .url(path)
                .content(contentType ?? 'application/json')
                .query(queryParams, { omitUndefinedOrNullValues: true });

            const methodRequest = httpMethod === "get" || httpMethod === "delete" ? request[httpMethod]() : request[httpMethod](body);
            const response: WretchResponse = await methodRequest.res();

            let json: any | undefined = undefined;
            let blob: any | undefined = undefined;
            if (response.status !== 204) {
                try {
                    if (conf.asBlob) {
                        blob = await response.blob();
                    } else {
                        json = await response.json();
                    }
                } catch (err: any) {
                    if (err.message == 'Unexpected end of JSON input') {
                        json = null;
                    } else {
                        throw err;
                    }
                }
            }

            return {
                status: response.status,
                headers: response.headers,
                json,
                blob
            };

        } catch (err: any) {
            const response: WretchResponse = err.response as WretchResponse;
            const json: any = (err.text != null && hasText(err.text)) ? JSON.parse(err.text) : {};

            if (response.status === 401) {
                store.dispatch(doLogout())
                store.dispatch(
                    addSnackbar(
                        'Credentials have expired. Please log in again.',
                        'credExpired'
                    )
                )
                throw new Error("Unauthorized")
            }

            return {
                status: response.status,
                headers: response.headers,
                json,
                blob: undefined
            };
            // throw {
            //     status: response.status,
            //     json
            // };
        } finally {
            // add an entry into NetworkLog;
        }
    }

/**
 * GET -----------------------------------------------
 *
 * @param pathTpl a template from OpenAPI doc
 * @param pathParams path params
 * @param queryParams query params
 */
const httpGet: (
    pathTpl: string,
    pathParams: Dictionary<string>,
    queryParams: Dictionary<string>,
    conf?: RequestConf
) => Promise<GenericHttpResponse> = async (
    pathTpl,
    pathParams,
    queryParams,
    conf = {}
) => {
        return runMethod("get", pathTpl, pathParams, queryParams, null, undefined, conf);
    }

/**
 * POST -----------------------------------------------
 *
 * @param pathTpl a template from OpenAPI doc
 * @param pathParams path params
 * @param queryParams query params
 * @param body body
 */
const httpPost: (
    pathTpl: string,
    pathParams: Dictionary<string>,
    queryParams: Dictionary<string>,
    body: any,
    conf?: RequestConf
) => Promise<GenericHttpResponse> = async (
    pathTpl,
    pathParams,
    queryParams,
    body,
    conf = {}
) => {
        return runMethod("post", pathTpl, pathParams, queryParams, body, undefined, conf);
    }

/**
 * PUT -----------------------------------------------
 * 
 * @param pathTpl a template from OpenAPI doc 
 * @param pathParams path params
 * @param queryParams query params
 * @param body body
 */
const httpPut: (
    pathTpl: string,
    pathParams: Dictionary<string>,
    queryParams: Dictionary<string>,
    body: any,
    conf?: RequestConf
) => Promise<GenericHttpResponse> = async (
    pathTpl,
    pathParams,
    queryParams,
    body,
    conf = {}
) => {
        return runMethod("put", pathTpl, pathParams, queryParams, body, 'text/uri-list', conf);
    }

/**
 * DELETE -----------------------------------------------
 *
 * @param pathTpl a template from OpenAPI doc
 * @param pathParams path params
 * @param queryParams query params
 * @param body body
 */
const httpDelete: (
    pathTpl: string,
    pathParams: Dictionary<string>,
    queryParams: Dictionary<string>,
    conf?: RequestConf
) => Promise<GenericHttpResponse> = async (
    pathTpl,
    pathParams,
    queryParams,
    conf = {}
) => {
        return runMethod("delete", pathTpl, pathParams, queryParams, {}, undefined, conf);
    }

/**
 * PATCH -----------------------------------------------
 *
 * @param pathTpl a template from OpenAPI doc
 * @param pathParams path params
 * @param queryParams query params
 * @param body body
 */
const httpPatch: (
    pathTpl: string,
    pathParams: Dictionary<string>,
    queryParams: Dictionary<string>,
    body: any,
    conf?: RequestConf
) => Promise<GenericHttpResponse> = async (
    pathTpl,
    pathParams,
    queryParams,
    body,
    conf = {}
) => {
        return runMethod("patch", pathTpl, pathParams, queryParams, body, 'application/json-patch+json', conf);
    }

export { httpGet, httpPost, httpPut, httpPatch, httpDelete }
